import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { fetchPlacements } from 'core/actions/ocapi';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import { selectSiteUrl, selectStoreCount, selectPageName } from 'core/selectors/app';
import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';
import { selectUserIsIdentified } from 'core/selectors/user';
import ContentWrap from 'core/modules/Partials/ContentWrap/ContentWrap';
import List from 'core/modules/Partials/List/List';
import MerchantTileListItem from 'core/modules/MerchantTileList/components/MerchantTileListItem/MerchantTileListItem';

import {
  triggerFeaturedOffersLogoIsViewed,
  triggerFeaturedOffersLogoIsSwiped,
  triggerViewAllStoresIsViewed,
} from './actions';

import {
  selectFeaturedOffersLogoConfig,
  selectFeaturedOffersLogoIsLoading,
  selectFeaturedOffersLogoIsLoaded,
  selectFeaturedOffersLogoRecords,
} from './selectors';

import './FeaturedOffersLogo.scss';

function FeaturedOffersLogo({ customParams, label, isPersonalizedMhp }) {
  const isLoaded = useSelector(selectFeaturedOffersLogoIsLoaded);
  const isLoading = useSelector(selectFeaturedOffersLogoIsLoading);
  const siteUrl = useSelector(selectSiteUrl);
  const config = useSelector(selectFeaturedOffersLogoConfig);
  const merchants = useSelector(selectFeaturedOffersLogoRecords);
  const isMobileMQ = useSelector(selectIsMobileMQ);
  const storeCount = useSelector(selectStoreCount);
  const userIsIdentified = useSelector(selectUserIsIdentified);
  const pageName = useSelector(selectPageName);
  const dispatch = useDispatch();
  const isHomePage = pageName === 'h';
  const {
    useAddToFavorite, shouldHideIncompleteRow, getViewAllLinkUrl, viewAllLinkTitle, getTitle,
  } = config;
  const sectionTitle = getTitle(storeCount);
  const viewAllLinkUrl = getViewAllLinkUrl(siteUrl);

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      const { api: { options, params } } = config;
      dispatch(fetchPlacements({ ...params, ...customParams }, options));
    }
  }, []);

  useEffect(() => {
    if (isLoaded && merchants.length) {
      if (!userIsIdentified && isHomePage && !!viewAllLinkUrl) {
        dispatch(triggerViewAllStoresIsViewed());
      }
      dispatch(triggerFeaturedOffersLogoIsViewed(merchants));
    }
  }, [merchants, isLoaded, isHomePage, userIsIdentified, dispatch]);

  const onCarouselSettle = (event) => {
    const slideIndex = event.selectedScrollSnap();
    dispatch(triggerFeaturedOffersLogoIsSwiped(slideIndex + 1, storeCount));
  };

  if (isEmpty(merchants)) {
    return null;
  }

  return (
    <div className="mn_featuredOffersLogo">
      <ContentWrap mobile tablet l xl xxl={!isPersonalizedMhp}>
        <SectionTitle
          title={sectionTitle}
          viewAllLinkUrl={viewAllLinkUrl}
          viewAllLinkTitle={viewAllLinkTitle}
        />
        <List
          shouldUseCarousel={isMobileMQ}
          shouldHideIncompleteRow={shouldHideIncompleteRow}
          onSettle={onCarouselSettle}
        >
          {merchants.map(merchant => (
            <MerchantTileListItem
              key={merchant.id}
              merchant={merchant}
              useAddToFavorite={useAddToFavorite}
              label={label}
            />
          ))}
        </List>
      </ContentWrap>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><FeaturedOffersLogo {...props} /></ComponentErrorBoundary>;
